import { useAuth0 } from '@auth0/auth0-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import { useToast } from '../../../context/ToastContext';
import { generateBookingFromReceipt } from '../apis/bookingGenerationApi';

/**
 * Hook to trigger booking generation for a single receipt.
 * On success, invalidates the uploaded-receipts cache so polling picks up the status change.
 */
export const useGenerateReceiptBooking = () => {
  const { getAccessTokenSilently } = useAuth0();
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  const mutation = useMutation({
    mutationFn: async (receiptId: string) => {
      const token = await getAccessTokenSilently();
      return generateBookingFromReceipt(receiptId, token);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['uploaded-receipts'] });
    },
  });

  const generateBooking = useCallback(
    async (receiptId: string): Promise<boolean> => {
      try {
        await mutation.mutateAsync(receiptId);
        return true;
      } catch (error) {
        console.error('Failed to generate booking:', error);
        const errorMessage =
          error instanceof Error
            ? error.message
            : 'Failed to generate booking. Please try again.';
        showToast('Generation Failed', errorMessage, 'error');
        return false;
      }
    },
    [mutation, showToast]
  );

  return {
    generateBooking,
    isGenerating: mutation.isPending,
  };
};
